import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import LockClockRoundedIcon from '@mui/icons-material/LockClockRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import Box from '@mui/material/Box';
import { motion } from 'framer-motion';
import type { ComponentType } from 'react';
import { getErrorCopy } from '@/Pages/Error/copy';
import { useErrorAnimation } from '@/Pages/Error/hooks/useErrorAnimation';
import type { ErrorType } from '@/Pages/Error/types/errorLocationState';
import '@/Pages/Error/ErrorPage.sass';

const ERROR_ICONS: Record<
  ErrorType,
  ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
> = {
  generic: ErrorOutlineRoundedIcon,
  network: WifiOffRoundedIcon,
  auth: LockClockRoundedIcon,
  server: CloudOffRoundedIcon,
  not_found: SearchOffRoundedIcon,
  already_onboarded: HowToRegRoundedIcon,
};

type ErrorIconProps = {
  errorType: ErrorType;
};

function ErrorIconContent({ errorType }: ErrorIconProps): JSX.Element {
  const Icon = ERROR_ICONS[errorType];
  return <Icon className="error-icon__glyph" aria-hidden />;
}

export function ErrorIcon({ errorType }: ErrorIconProps): JSX.Element {
  const { reduceMotion, icon } = useErrorAnimation();
  const iconAlt = getErrorCopy(errorType).iconAlt;

  if (reduceMotion) {
    return (
      <Box
        className="error-icon"
        data-testid="error-icon"
        data-error-type={errorType}
        data-motion="reduced"
        role="img"
        aria-label={iconAlt}
      >
        <ErrorIconContent errorType={errorType} />
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      className="error-icon"
      data-testid="error-icon"
      data-error-type={errorType}
      data-motion="animated"
      role="img"
      aria-label={iconAlt}
      initial={icon.initial}
      animate={icon.animate}
      transition={icon.transition}
    >
      <ErrorIconContent errorType={errorType} />
    </Box>
  );
}
